/**
 * Whom a manager is looking at, in the room and in the Bug Hunt: everybody, a team (with its
 * squads), a squad, or one developer.
 *
 * Only for managers — the teams and developers lists are theirs to read — and only worth showing
 * when there is more than one team or more than one person. The choice is remembered per browser
 * and shared by both screens, so going from the room to the Bug Hunt keeps looking at the same
 * people. A remembered team or person that no longer exists reads as "everybody", not as an empty
 * screen.
 */

import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { atLeast, getSession } from './auth'
import type { Developer, Team, TeamRef } from './types'

const DEFAULT_KEY = 'devpulse:team-filter'

export type Scope = { type: 'team'; id: string } | { type: 'developer'; id: string }

/** "team:<id>" or "dev:<id>". A bare id is a team, as the first version stored it. */
export function parseScope(value: string | null): Scope | null {
  if (!value) return null
  if (value.startsWith('dev:')) return { type: 'developer', id: value.slice(4) }
  if (value.startsWith('team:')) return { type: 'team', id: value.slice(5) }
  return { type: 'team', id: value }
}

export function formatScope(scope: Scope | null): string {
  if (!scope) return ''
  return scope.type === 'developer' ? `dev:${scope.id}` : `team:${scope.id}`
}

function remembered(key: string): Scope | null {
  try {
    return parseScope(localStorage.getItem(key))
  } catch {
    return null
  }
}

function remember(key: string, scope: Scope | null): void {
  try {
    if (scope) localStorage.setItem(key, formatScope(scope))
    else localStorage.removeItem(key)
  } catch {
    // Storage blocked: the choice still holds for this visit.
  }
}

export interface TeamFilter {
  teams: Team[]
  developers: Developer[]
  scope: Scope | null
  setScope: (scope: Scope | null) => void
  /** The team being looked at, or undefined. */
  teamId: string | undefined
  /** The developer being looked at, or undefined. */
  developerId: string | undefined
  /** The teams and squads of the developer being looked at. */
  developerTeams: TeamRef[]
  /** Whether a picker is worth drawing: a manager, and a choice to make. */
  show: boolean
  /** False until the lists are known, so a screen can wait rather than load everybody first. */
  ready: boolean
}

/** @param storageKey Where the choice is remembered. The room and the Bug Hunt share one; reports keep their own. */
export function useTeamFilter(storageKey: string = DEFAULT_KEY): TeamFilter {
  const canManage = atLeast(getSession()?.role, 'EngineeringManager')

  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: api.settings.teams, enabled: canManage, staleTime: 60_000 })
  const developersQuery = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers, enabled: canManage, staleTime: 60_000 })

  const [stored, setStored] = useState<Scope | null>(() => remembered(storageKey))
  const teams = teamsQuery.data ?? []
  const developers = (developersQuery.data ?? []).filter((d) => d.isActive)

  const scope =
    stored?.type === 'team' && teams.some((t) => t.id === stored.id) ? stored
      : stored?.type === 'developer' && developers.some((d) => d.id === stored.id) ? stored
        : null

  const setScope = useCallback((next: Scope | null) => {
    setStored(next)
    remember(storageKey, next)
  }, [storageKey])

  const developer = scope?.type === 'developer' ? developers.find((d) => d.id === scope.id) : undefined

  return {
    teams,
    developers,
    scope,
    setScope,
    teamId: scope?.type === 'team' ? scope.id : undefined,
    developerId: scope?.type === 'developer' ? scope.id : undefined,
    developerTeams: developer?.teams ?? [],
    show: canManage && (teams.length > 1 || developers.length > 1),
    // Nothing remembered, or not a manager: nothing to wait for.
    ready: !canManage || !stored || (teamsQuery.isFetched && developersQuery.isFetched),
  }
}
